import { getBusinessUnitConfig } from '@/modules/bu/registry.base';
import { useAuthStore } from '@/stores/auth.store';
import { useOrderSummaryStore } from '@/stores/orderSummary.store';
import { getEffectiveBu, normalizeBusinessUnit } from '@/utils/bu.utils';
import { computed } from 'vue';

/**
 * The product whose contact chrome to show: support contacts, the WhatsApp
 * link, the "About <product>" footer, the privacy link, the terms sentence.
 *
 * Two signals, in order, and no third:
 *
 * 1. The BU path prefix or production domain. Unambiguous when present.
 * 2. The active order's `business_unit`, the signal the `/cart` route guard
 *    already trusts, for the shared routes that have no prefix (`/cart/*`,
 *    `/account/settings/*`, `/terms`, the 404).
 *
 * Deliberately NOT the stored `cusp_bu` that `useBusinessUnitResolver()` falls
 * back to. `setStoredBu()` has a single caller — the BU chooser — and nothing
 * rewrites it on an order switch, so on a cold load of a shared route it names
 * whichever product the user picked last, which may not be the one they are
 * in. Callers render their neutral default when this resolves to nothing, and
 * a neutral footer beats confidently showing another product's WhatsApp.
 *
 * The order is only read inside a session that holds a token: nothing clears
 * the order summary store on logout, so an unauthenticated read could inherit
 * the previous user's product.
 *
 * `useBusinessUnitResolver()` is left alone on purpose. Its `current` and
 * `inConflict` drive routing, login redirects and BU enforcement, which is a
 * different blast radius than contact chrome. FLOK-1646.
 */
export const useProductBusinessUnit = () => {
  const route = useRoute();
  const authStore = useAuthStore();
  const orderSummaryStore = useOrderSummaryStore();

  const config = computed(() => {
    const explicit_bu = getEffectiveBu({
      path_value: route.path,
      origin_value: window.location.origin,
    });
    if (explicit_bu) return getBusinessUnitConfig(explicit_bu);

    if (!authStore.access_token) return undefined;

    return getBusinessUnitConfig(
      normalizeBusinessUnit(orderSummaryStore.orderSummary?.business_unit),
    );
  });

  return { config };
};
